import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { Alert, AlertDescription } from "../ui/alert";
import {
  Skeleton,
  SkeletonCard,
  SkeletonTable,
  SkeletonKPICard,
} from "../ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Plus } from "lucide-react";
import { useContextDetection } from "../../hooks/useContextDetection";
import { UnifiedRiskService } from "../../services/unifiedServices";
import { BusinessUnitService } from "../../services/businessUnitService";
import CreateRiskForm from "../Risk/CreateRiskForm";
import EditRiskForm from "../Risk/EditRiskForm";
import RiskTable from "../Risk/RiskTable";
import RiskStats from "../Risk/RiskStats";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Separator } from "../ui/separator";
import { Link } from "lucide-react";
import { RiskService } from "../../services/riskService";
import PageHeader from "../Common/PageHeader";
import { getApiUrl } from "../../config/api";

const UnifiedRisksPage = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const {
    contextId,
    contextType,
    organizationId,
    businessUnitId,
    isOrganizationContext,
    canManage,
  } = useContextDetection();

  const [contextData, setContextData] = useState(null);
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateRiskOpen, setIsCreateRiskOpen] = useState(false);
  const [isEditRiskOpen, setIsEditRiskOpen] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [isImportRiskOpen, setIsImportRiskOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [availableImportRisks, setAvailableImportRisks] = useState([]);
  const [selectedImportRisks, setSelectedImportRisks] = useState(new Set());
  const [importSubmitting, setImportSubmitting] = useState(false);

  // Get context-specific data (organization or business unit details)
  const fetchContextData = async () => {
    if (isOrganizationContext && organizationId) {
      try {
        const response = await fetch(
          getApiUrl(`organizations/${organizationId}`),
          {
            headers: {
              Authorization: `Bearer ${await getAccessTokenSilently()}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch organization");
        }

        const data = await response.json();
        setContextData(data);
      } catch (err) {
        console.error("Error fetching organization data:", err);
      }
    } else if (!isOrganizationContext && businessUnitId) {
      try {
        const data = await BusinessUnitService.getBusinessUnitDetails(
          businessUnitId,
          getAccessTokenSilently
        );
        setContextData(data);
      } catch (err) {
        console.error("Error fetching business unit data:", err);
      }
    }
  };

  const fetchRisks = async () => {
    if (!contextId) return;

    try {
      setLoading(true);
      const data = await UnifiedRiskService.getRisks(
        contextType,
        contextId,
        organizationId || contextData?.organization_id,
        getAccessTokenSilently
      );
      setRisks(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching risks:", err);
      setError(err.message);
      setRisks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (contextId) {
      fetchContextData();
    }
  }, [contextId, isOrganizationContext]);

  useEffect(() => {
    if (contextId && contextData) {
      fetchRisks();
    }
  }, [contextId, contextData]);

  const handleRiskCreated = () => {
    setIsCreateRiskOpen(false);
    fetchRisks();
  };

  const handleEditRisk = (risk) => {
    // Ensure business unit ID is included for business unit risks
    const riskWithContext = isOrganizationContext
      ? risk
      : {
          ...risk,
          business_unit_id: businessUnitId,
        };
    setSelectedRisk(riskWithContext);
    setIsEditRiskOpen(true);
  };

  const handleRiskUpdated = () => {
    setIsEditRiskOpen(false);
    setSelectedRisk(null);
    fetchRisks();
  };

  const handleDeleteRisk = async (risk) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the risk: "${
          risk.name || risk.description
        }"?`
      )
    ) {
      return;
    }

    try {
      await UnifiedRiskService.deleteRisk(risk.id, getAccessTokenSilently);
      fetchRisks();
    } catch (err) {
      console.error("Error deleting risk:", err);
      setError("Failed to delete risk. Please try again.");
    }
  };

  // Fetch available org-level risks for import
  const fetchAvailableImportRisks = async () => {
    if (!businessUnitId) return;
    try {
      setImportLoading(true);
      setImportError(null);
      const data = await RiskService.getAvailableRisksForAssociation(
        businessUnitId,
        getAccessTokenSilently
      );
      setAvailableImportRisks(data);
    } catch (err) {
      setImportError(err.message);
      setAvailableImportRisks([]);
    } finally {
      setImportLoading(false);
    }
  };

  // Open import modal and fetch risks
  const handleOpenImport = () => {
    setIsImportRiskOpen(true);
    setSelectedImportRisks(new Set());
    fetchAvailableImportRisks();
  };

  // Handle risk selection toggle
  const handleToggleImportRisk = (riskId) => {
    setSelectedImportRisks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(riskId)) newSet.delete(riskId);
      else newSet.add(riskId);
      return newSet;
    });
  };

  // Import selected risks
  const handleImportRisks = async () => {
    if (selectedImportRisks.size === 0) {
      setImportError("Please select at least one risk to import.");
      return;
    }
    setImportSubmitting(true);
    setImportError(null);
    try {
      const promises = Array.from(selectedImportRisks).map((riskId) =>
        RiskService.associateOrganizationRisk(
          { organization_risk_id: riskId, business_unit_id: businessUnitId },
          getAccessTokenSilently
        )
      );
      await Promise.all(promises);
      setIsImportRiskOpen(false);
      setSelectedImportRisks(new Set());
      fetchRisks();
    } catch (err) {
      setImportError(err.message);
    } finally {
      setImportSubmitting(false);
    }
  };

  // Generate context-aware page title
  const getPageTitle = () => {
    return "Risks";
  };

  const getPageSubtitle = () => {
    if (isOrganizationContext) {
      return "Manage risks for this organization";
    } else {
      return "Manage risks for this business unit";
    }
  };

  if (loading) {
    return (
      <div className="p-10 w-full">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between mb-8">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-9 w-28" />
          </div>
        </div>

        {/* Stats Skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonKPICard key={index} />
          ))}
        </div>

        {/* Table Skeleton */}
        <SkeletonCard>
          <div className="space-y-4">
            <Skeleton className="h-6 w-48" />
            <SkeletonTable rows={8} columns={6} />
          </div>
        </SkeletonCard>
      </div>
    );
  }

  return (
    <div className="p-10 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <PageHeader
          pageName={getPageTitle()}
          entityName={contextData?.name}
          entityType={isOrganizationContext ? "organization" : "business unit"}
        />
        {canManage && (
          <div className="flex gap-2">
            {!isOrganizationContext && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleOpenImport}
                className="flex items-center gap-2"
              >
                <Link className="h-4 w-4" />
                Import Risk from Organization
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => setIsCreateRiskOpen(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Create Risk
            </Button>
          </div>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-8">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Risk Stats */}
      <RiskStats risks={risks} />

      {/* Risks Table */}
      <RiskTable
        risks={risks}
        canManage={canManage}
        onEditRisk={handleEditRisk}
        onDeleteRisk={handleDeleteRisk}
        onCreateRisk={() => setIsCreateRiskOpen(true)}
      />

      {/* Create Risk Dialog */}
      <Dialog open={isCreateRiskOpen} onOpenChange={setIsCreateRiskOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <CreateRiskForm
            open={isCreateRiskOpen}
            onClose={() => setIsCreateRiskOpen(false)}
            organizationId={organizationId || contextData?.organization_id}
            businessUnitId={isOrganizationContext ? null : businessUnitId}
            onRiskCreated={handleRiskCreated}
            isOrgAdmin={canManage}
            forceOrganizationLevel={isOrganizationContext}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Risk Dialog */}
      <EditRiskForm
        open={isEditRiskOpen}
        onClose={() => setIsEditRiskOpen(false)}
        risk={selectedRisk}
        organizationId={organizationId || contextData?.organization_id}
        businessUnitId={isOrganizationContext ? null : businessUnitId}
        onRiskUpdated={handleRiskUpdated}
      />

      {/* Import Risk Modal */}
      <Dialog open={isImportRiskOpen} onOpenChange={setIsImportRiskOpen}>
        <DialogContent className="max-w-2xl max-h-[600px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Risk from Organization</DialogTitle>
            <DialogDescription>
              Select organization-level risks to import into this business unit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {importLoading ? (
              <div className="flex justify-center p-8">
                <Spinner />
              </div>
            ) : (
              <>
                {importError && (
                  <Alert variant="destructive">{importError}</Alert>
                )}
                {availableImportRisks.length === 0 ? (
                  <div className="text-center py-8">
                    <h3 className="text-lg font-semibold text-muted-foreground">
                      No Organization Risks Available
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2">
                      All organization-level risks have already been imported,
                      or none exist.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Select risks to import. Selected:{" "}
                      {selectedImportRisks.size}
                    </p>
                    <div className="space-y-3">
                      {availableImportRisks.map((risk) => {
                        // Simple risk level info for badge
                        let badgeVariant = "secondary";
                        let badgeLabel = "Low";
                        if (risk.risk_level >= 15) {
                          badgeVariant = "destructive";
                          badgeLabel = "Critical";
                        } else if (risk.risk_level >= 9) {
                          badgeVariant = "secondary";
                          badgeLabel = "High";
                        } else if (risk.risk_level >= 5) {
                          badgeVariant = "outline";
                          badgeLabel = "Medium";
                        }
                        const isSelected = selectedImportRisks.has(risk.id);
                        return (
                          <div
                            key={risk.id}
                            className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                              isSelected
                                ? "border-primary bg-primary/5"
                                : "border-border hover:bg-muted/50"
                            }`}
                            onClick={() => handleToggleImportRisk(risk.id)}
                          >
                            <div className="flex items-start gap-3">
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleToggleImportRisk(risk.id)}
                                className="mt-1"
                              />
                              <div className="flex-1 space-y-2">
                                <div className="flex items-start justify-between">
                                  <h4 className="font-semibold">
                                    {risk.name ||
                                      risk.description ||
                                      risk.title}
                                  </h4>
                                  <Badge
                                    variant={badgeVariant}
                                    className="flex items-center gap-1"
                                  >
                                    {badgeLabel} ({risk.risk_level})
                                  </Badge>
                                </div>
                                {risk.description &&
                                  risk.name !== risk.description && (
                                    <p className="text-sm text-muted-foreground mb-2">
                                      {risk.description}
                                    </p>
                                  )}
                                <p className="text-sm text-muted-foreground">
                                  {risk.Category?.name
                                    ? `Type: ${risk.Category.name}`
                                    : null}
                                </p>
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                  <span>
                                    <strong>Severity:</strong>{" "}
                                    {risk.Severity?.name ||
                                      risk.Severity?.description}{" "}
                                    (Level {risk.Severity?.value})
                                  </span>
                                  <span>
                                    <strong>Likelihood:</strong>{" "}
                                    {risk.Likelihood?.description} (Level{" "}
                                    {risk.Likelihood?.value})
                                  </span>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  <strong>Created by:</strong>{" "}
                                  {risk.Creator?.name || "Unknown"} on{" "}
                                  {risk.created_at
                                    ? new Date(
                                        risk.created_at
                                      ).toLocaleDateString()
                                    : "N/A"}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <Separator />
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>
                        <strong>Note:</strong> Importing will create business
                        unit-specific copies of these risks.
                      </p>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsImportRiskOpen(false)}
              disabled={importSubmitting}
            >
              Cancel
            </Button>
            {availableImportRisks.length > 0 && (
              <Button
                onClick={handleImportRisks}
                disabled={importSubmitting || selectedImportRisks.size === 0}
              >
                {importSubmitting ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4" />
                    Importing...
                  </>
                ) : (
                  `Import ${selectedImportRisks.size} Risk${
                    selectedImportRisks.size !== 1 ? "s" : ""
                  }`
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnifiedRisksPage;
